import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getInvoices,
  getExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
} from '../services/api';
import {
  showErrorToast,
  showLoadingToast,
  updateLoadingToastSuccess,
  updateLoadingToastError,
} from '../utils/toastConfig';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSearch,
  FiTrendingUp, FiTrendingDown, FiFileText,
  FiArrowUpRight, FiArrowDownRight, FiActivity,
} from 'react-icons/fi';
import { MdCurrencyRupee } from 'react-icons/md';
import CustomDatePicker from '../components/CustomDatePicker';
import './Account.css';

const fmtINR = (n) =>
  '₹' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const normalizeInvNum = (num) => {
  if (!num || !num.includes('/')) return num;
  const parts = num.split('/');
  return `VIECPL${parts[parts.length - 1]}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// MySQL DATE columns come back as JS Date objects (via mysql2). Normalize
// both Date instances and any ISO/locale strings to a plain "YYYY-MM-DD"
// suitable for <input type="date">.
const toISODate = (val) => {
  if (!val) return todayISO();
  const d = val instanceof Date ? val : new Date(val);
  return Number.isNaN(d.getTime()) ? todayISO() : d.toISOString().slice(0, 10);
};

const emptyExpenseForm = { title: '', amount: '', expense_date: todayISO(), description: '' };

export default function Account() {
  const { user } = useAuth();
  const isCEO = user?.role === 'ceo' || user?.role === 'admin';
  const isHR = user?.role === 'hr';
  const canManage = isCEO || isHR;

  const [paidInvoices, setPaidInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search across the two tables
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [expenseSearch, setExpenseSearch] = useState('');

  // Expense modal
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState(emptyExpenseForm);
  const [savingExpense, setSavingExpense] = useState(false);

  // Delete confirmation
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [deletingExpense, setDeletingExpense] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [invRes, expRes] = await Promise.all([getInvoices(), getExpenses()]);
      const paid = (invRes.data || []).filter(
        (i) => i.payment_status === 'Paid' && !i.is_cancelled
      );
      setPaidInvoices(paid);
      setExpenses(expRes.data || []);
    } catch (err) {
      showErrorToast('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Refetch when the tab regains focus / becomes visible so payment-status
  // changes made on the Dashboard (or in another tab) flow into the totals.
  useEffect(() => {
    const onFocus = () => loadAll();
    const onVisibility = () => { if (!document.hidden) loadAll(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Tally — computed off the latest data so it always reflects status/expense changes
  const totals = useMemo(() => {
    const totalPaid = paidInvoices.reduce(
      (sum, inv) => sum + parseFloat(inv.total_amount || 0),
      0
    );
    const totalExpenses = expenses.reduce(
      (sum, e) => sum + parseFloat(e.amount || 0),
      0
    );
    return {
      totalPaid,
      totalExpenses,
      balance: totalPaid - totalExpenses,
    };
  }, [paidInvoices, expenses]);

  const filteredPaid = paidInvoices.filter((inv) => {
    if (!invoiceSearch.trim()) return true;
    const q = invoiceSearch.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(q) ||
      inv.client_name?.toLowerCase().includes(q) ||
      parseFloat(inv.total_amount).toString().includes(q)
    );
  });

  const filteredExpenses = expenses.filter((e) => {
    if (!expenseSearch.trim()) return true;
    const q = expenseSearch.toLowerCase();
    return (
      e.title?.toLowerCase().includes(q) ||
      (e.description || '').toLowerCase().includes(q) ||
      parseFloat(e.amount).toString().includes(q)
    );
  });

  // ── Expense handlers ───────────────────────────────────
  const openCreateExpense = () => {
    setEditingExpense(null);
    setExpenseForm(emptyExpenseForm);
    setShowExpenseModal(true);
  };

  const openEditExpense = (exp) => {
    setEditingExpense(exp);
    setExpenseForm({
      title: exp.title || '',
      amount: exp.amount?.toString() || '',
      expense_date: toISODate(exp.expense_date),
      description: exp.description || '',
    });
    setShowExpenseModal(true);
  };

  const handleSaveExpense = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!expenseForm.title.trim()) {
      showErrorToast('Title is required');
      return;
    }
    const amt = parseFloat(expenseForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      showErrorToast('Please enter a valid amount');
      return;
    }
    if (!expenseForm.expense_date) {
      showErrorToast('Date is required');
      return;
    }
    setSavingExpense(true);
    const toastId = showLoadingToast(editingExpense ? 'Updating expense...' : 'Adding expense...');
    try {
      const payload = {
        title: expenseForm.title.trim(),
        amount: amt,
        expense_date: expenseForm.expense_date,
        description: expenseForm.description.trim() || null,
      };
      if (editingExpense) {
        await updateExpense(editingExpense.id, payload);
        updateLoadingToastSuccess(toastId, 'Expense updated');
      } else {
        await createExpense(payload);
        updateLoadingToastSuccess(toastId, 'Expense added');
      }
      setShowExpenseModal(false);
      setEditingExpense(null);
      setExpenseForm(emptyExpenseForm);
      loadAll();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed to save expense');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleConfirmDeleteExpense = async () => {
    if (!expenseToDelete) return;
    setDeletingExpense(true);
    const toastId = showLoadingToast('Deleting expense...');
    try {
      await deleteExpense(expenseToDelete.id);
      updateLoadingToastSuccess(toastId, 'Expense deleted');
      setExpenseToDelete(null);
      loadAll();
    } catch (err) {
      updateLoadingToastError(toastId, err.response?.data?.message || 'Failed to delete expense');
    } finally {
      setDeletingExpense(false);
    }
  };

  return (
    <div className="dashboard acc-page">
      {/* Page header */}
      <header className="acc-page-header">
        <div className="acc-page-header-left">
          <span className="acc-page-eyebrow">FINANCE</span>
          <h1 className="acc-page-title">Account</h1>
          <p className="acc-page-sub">
            Track paid revenue, manage expenses, and monitor your net balance in one place.
          </p>
        </div>
      </header>

      <main className="acc-main">
        {/* Tally summary */}
        <section className="acc-summary-row" aria-label="Financial summary">
          <article className="acc-card acc-card-income">
            <div className="acc-card-top">
              <div className="acc-card-icon"><FiArrowUpRight size={22} /></div>
              <span className="acc-card-pill acc-card-pill-income">Income</span>
            </div>
            <div className="acc-card-body">
              <span className="acc-card-label">Total Paid</span>
              <h3 className="acc-card-value">{fmtINR(totals.totalPaid)}</h3>
              <span className="acc-card-meta">
                <FiFileText size={11} />
                {paidInvoices.length} paid invoice{paidInvoices.length === 1 ? '' : 's'}
              </span>
            </div>
          </article>

          <article className="acc-card acc-card-expense">
            <div className="acc-card-top">
              <div className="acc-card-icon"><FiArrowDownRight size={22} /></div>
              <span className="acc-card-pill acc-card-pill-expense">Outflow</span>
            </div>
            <div className="acc-card-body">
              <span className="acc-card-label">Total Expenses</span>
              <h3 className="acc-card-value">{fmtINR(totals.totalExpenses)}</h3>
              <span className="acc-card-meta">
                <FiTrendingDown size={11} />
                {expenses.length} entr{expenses.length === 1 ? 'y' : 'ies'}
              </span>
            </div>
          </article>

          <article className={`acc-card acc-card-balance ${totals.balance < 0 ? 'acc-card-negative' : ''}`}>
            <div className="acc-card-top">
              <div className="acc-card-icon"><MdCurrencyRupee size={22} /></div>
              <span className={`acc-card-pill ${totals.balance < 0 ? 'acc-card-pill-warn' : 'acc-card-pill-balance'}`}>
                {totals.balance < 0 ? 'Deficit' : 'Net'}
              </span>
            </div>
            <div className="acc-card-body">
              <span className="acc-card-label">Net Balance</span>
              <h3 className="acc-card-value">{fmtINR(totals.balance)}</h3>
              <span className="acc-card-meta">
                <FiActivity size={11} />
                Income − Expenses
              </span>
            </div>
          </article>
        </section>

        {/* Paid invoices */}
        <section className="acc-section recent-section">
          <div className="rs-header acc-section-header">
            <div className="rs-header-left">
              <div className="rs-title-row">
                <span className="acc-section-dot acc-section-dot-income">
                  <FiTrendingUp size={14} />
                </span>
                <h2>Paid Invoices</h2>
                <span className="rs-count-badge">{filteredPaid.length}</span>
              </div>
              <p className="rs-subtitle">Invoices marked as <strong>Paid</strong> contribute to income.</p>
            </div>
            <div className="rs-header-controls">
              <div className="rs-search-wrap">
                <FiSearch className="rs-search-icon" />
                <input
                  type="text"
                  className="rs-search-input"
                  placeholder="Search paid invoices..."
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                />
                {invoiceSearch && (
                  <button className="rs-search-clear" onClick={() => setInvoiceSearch('')}>
                    <FiX size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="rs-empty acc-empty">
              <div className="rs-empty-icon acc-empty-icon"><FiActivity size={32} /></div>
              <h4>Loading account data…</h4>
            </div>
          ) : filteredPaid.length === 0 ? (
            <div className="rs-empty acc-empty">
              <div className="rs-empty-icon acc-empty-icon"><FiFileText size={32} /></div>
              <h4>{invoiceSearch ? 'No matches' : 'No paid invoices yet'}</h4>
              <p>
                {invoiceSearch
                  ? `No paid invoices match "${invoiceSearch}"`
                  : 'When invoices are marked as Paid from the Dashboard, they will show up here.'}
              </p>
            </div>
          ) : (
            <div className="rs-table-container acc-table-wrap">
              <table className="rs-table acc-table">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>S.No</th>
                    <th>Invoice</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Amount (incl. tax)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPaid.map((inv, idx) => (
                    <tr key={inv.id} className={idx % 2 === 0 ? 'row-even' : 'row-odd'}>
                      <td><span className="inv-sno">{idx + 1}</span></td>
                      <td><span className="inv-number acc-pill-num">{normalizeInvNum(inv.invoice_number)}</span></td>
                      <td><span className="inv-client">{inv.client_name}</span></td>
                      <td>
                        <span className="inv-date">
                          {new Date(inv.invoice_date).toLocaleDateString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })}
                        </span>
                      </td>
                      <td><span className="inv-total acc-total-income">{fmtINR(inv.total_amount)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Expenses */}
        <section className="acc-section recent-section">
          <div className="rs-header acc-section-header">
            <div className="rs-header-left">
              <div className="rs-title-row">
                <span className="acc-section-dot acc-section-dot-expense">
                  <FiTrendingDown size={14} />
                </span>
                <h2>Expenses</h2>
                <span className="rs-count-badge">{filteredExpenses.length}</span>
              </div>
              <p className="rs-subtitle">Add, edit, or remove expense entries.</p>
            </div>
            <div className="rs-header-controls">
              <div className="rs-search-wrap">
                <FiSearch className="rs-search-icon" />
                <input
                  type="text"
                  className="rs-search-input"
                  placeholder="Search expenses..."
                  value={expenseSearch}
                  onChange={(e) => setExpenseSearch(e.target.value)}
                />
                {expenseSearch && (
                  <button className="rs-search-clear" onClick={() => setExpenseSearch('')}>
                    <FiX size={14} />
                  </button>
                )}
              </div>
              {canManage && (
                <button className="acc-add-btn" onClick={openCreateExpense}>
                  <FiPlus size={16} />
                  <span>Add Expense</span>
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="rs-empty acc-empty">
              <div className="rs-empty-icon acc-empty-icon"><FiActivity size={32} /></div>
              <h4>Loading expenses…</h4>
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="rs-empty acc-empty">
              <div className="rs-empty-icon acc-empty-icon"><FiTrendingDown size={32} /></div>
              <h4>{expenseSearch ? 'No matches' : 'No expenses yet'}</h4>
              <p>
                {expenseSearch
                  ? `No expenses match "${expenseSearch}"`
                  : 'Click “Add Expense” to record your first one.'}
              </p>
            </div>
          ) : (
            <div className="rs-table-container acc-table-wrap">
              <table className="rs-table acc-table">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>S.No</th>
                    <th>Title</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th style={{ width: 110 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((exp, idx) => (
                    <tr key={exp.id} className={idx % 2 === 0 ? 'row-even' : 'row-odd'}>
                      <td><span className="inv-sno">{idx + 1}</span></td>
                      <td><span className="inv-client acc-exp-title">{exp.title}</span></td>
                      <td>
                        <span className="inv-date">
                          {new Date(exp.expense_date).toLocaleDateString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })}
                        </span>
                      </td>
                      <td><span className="inv-total acc-total-expense">{fmtINR(exp.amount)}</span></td>
                      <td>
                        <span className="acc-exp-desc">{exp.description || '—'}</span>
                      </td>
                      <td>
                        <div className="inv-actions">
                          {canManage ? (
                            <>
                              <button
                                className="inv-act-btn act-edit acc-icon-btn"
                                title="Edit expense"
                                onClick={() => openEditExpense(exp)}
                              >
                                <FiEdit2 size={14} />
                              </button>
                              <button
                                className="inv-act-btn act-delete acc-icon-btn acc-icon-btn-danger"
                                title="Delete expense"
                                onClick={() => setExpenseToDelete(exp)}
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </>
                          ) : (
                            <span className="acc-exp-desc">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {/* Expense Add/Edit modal */}
      {showExpenseModal && (
        <div className="dm-overlay" onClick={() => !savingExpense && setShowExpenseModal(false)}>
          <div className="acc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="acc-modal-header">
              <h3>{editingExpense ? 'Edit Expense' : 'Add Expense'}</h3>
              <button
                className="cm-close"
                onClick={() => setShowExpenseModal(false)}
                disabled={savingExpense}
              >
                <FiX size={18} />
              </button>
            </div>
            <form onSubmit={handleSaveExpense} className="acc-form">
              <div className="acc-form-field">
                <label>Title *</label>
                <input
                  type="text"
                  value={expenseForm.title}
                  onChange={(e) => setExpenseForm({ ...expenseForm, title: e.target.value })}
                  placeholder="e.g. Office rent — June"
                  maxLength={200}
                  required
                />
              </div>
              <div className="acc-form-grid">
                <div className="acc-form-field">
                  <label>Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="acc-form-field">
                  <label>Date *</label>
                  <CustomDatePicker
                    value={expenseForm.expense_date}
                    onChange={(date) => setExpenseForm({ ...expenseForm, expense_date: date })}
                  />
                </div>
              </div>
              <div className="acc-form-field">
                <label>Description / Remark</label>
                <textarea
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  placeholder="Optional notes"
                  rows={3}
                />
              </div>
              <div className="acc-modal-actions">
                <button
                  type="button"
                  className="dm-btn-cancel"
                  onClick={() => setShowExpenseModal(false)}
                  disabled={savingExpense}
                >
                  Cancel
                </button>
                <button type="submit" className="acc-btn-save" disabled={savingExpense}>
                  {savingExpense ? 'Saving...' : editingExpense ? 'Save Changes' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {expenseToDelete && (
        <div className="dm-overlay" onClick={() => !deletingExpense && setExpenseToDelete(null)}>
          <div className="dm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dm-icon"><FiTrash2 size={28} /></div>
            <h3>Delete Expense</h3>
            <p>
              Are you sure you want to delete <strong>{expenseToDelete.title}</strong>? This action cannot be undone.
            </p>
            <div className="dm-actions">
              <button
                className="dm-btn-cancel"
                onClick={() => setExpenseToDelete(null)}
                disabled={deletingExpense}
              >
                Cancel
              </button>
              <button
                className="dm-btn-delete"
                onClick={handleConfirmDeleteExpense}
                disabled={deletingExpense}
              >
                {deletingExpense ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
